import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart text label with count in parentheses.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubbleText - Visible label, e.g. "cart (2)".
 * @property {HTMLElement} cartBubbleA11y - Screen reader count text.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubbleText', 'cartBubbleA11y'];

  /** @type {string} */
  #a11yCountLabel = '';

  /** @type {number} */
  get currentCartCount() {
    const text = this.refs.cartBubbleText.textContent ?? '';
    const match = text.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatLabel(count) {
    if (count <= 0) return 'cart';

    const displayCount = count < 100 ? count : '99+';
    return `cart (${displayCount})`;
  }

  /**
   * @param {number} count
   */
  #setCartCount(count) {
    const safeCount = count < 0 ? 0 : count;

    this.refs.cartBubbleText.textContent = this.#formatLabel(safeCount);
    this.refs.cartBubbleA11y.textContent = `${this.#a11yCountLabel}: ${safeCount}`;
  }

  set currentCartCount(value) {
    this.#setCartCount(value);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#a11yCountLabel = (this.refs.cartBubbleA11y.textContent ?? '').split(':')[0]?.trim() ?? '';

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Updates the cart count label.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} [animate=true] - Whether to animate the count change.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.#setCartCount(nextCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);
    this.refs.cartBubbleText.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubbleText.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0 && count !== this.currentCartCount) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
